import React from 'react';
import { Group, Text } from '@mantine/core';
import { getDay, parseISO } from 'date-fns';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import { MaximumFeeDetail } from '../../../models/ParkingRate.ts';
import { DayOfWeek } from '../../../models/OperationHours.ts';
import {
  ALL_DOWS,
  DOW_SHORT,
  DOW_ZH,
  DaySlot,
  MaxFeeSlot,
  dateToShortLabel,
  parseRate,
} from '../../../utils/ParkingRateCardUtils.ts';

/** 單一時段列（read-only，用於 Step 3 費率預覽） */
const PreviewSlotRow: React.FC<{ slot: DaySlot }> = ({ slot }) => {
  const renderRate = () => {
    if (slot.rate === null) {
      return (
        <span style={{ fontSize: 12, color: '#b0b8c4', fontStyle: 'italic' }}>
          未知
        </span>
      );
    }
    const parsed = parseRate(slot.rate);
    if (parsed.kind === 'free') {
      return <span style={{ fontSize: 13, fontWeight: 700 }}>免費</span>;
    }
    if (parsed.kind === 'per_entry') {
      return (
        <Group gap={4}>
          <Text style={{ fontSize: 13, fontWeight: 700 }}>{parsed.fee}</Text>
          <Text style={{ fontSize: 11, color: '#9ca3af' }}>元 / 次</Text>
        </Group>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {parsed.tiers.map((t, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 24,
            }}
          >
            {t.timeLabel && (
              <span
                style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.timeLabel}
              </span>
            )}
            {t.isFree ? (
              <span style={{ fontSize: 13, fontWeight: 700 }}>免費</span>
            ) : (
              <Group gap={4}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  每 {t.intervalMinutes} 分鐘
                </span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{t.fee}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>元</span>
              </Group>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
        padding: '3px 0',
        gap: 8,
      }}
    >
      <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {slot.startTime} – {slot.endTime}
      </span>
      {renderRate()}
    </div>
  );
};

interface ParkingRatePreviewCardProps {
  ruleType: 'regular' | 'specific';
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  /** Regular 模式用 */
  dayCards?: {
    dows: DayOfWeek[];
    slots: DaySlot[];
    maxFeeSlots: MaxFeeSlot[];
  }[];
  /** Specific 模式用 */
  dateCards?: {
    dates: string[];
    slots: DaySlot[];
    maxFeeSlots: MaxFeeSlot[];
  }[];
  itemPerRow: number;
  isShowRatePeriods: boolean;
}

const ParkingRatePreviewCard: React.FC<ParkingRatePreviewCardProps> = ({
  ruleType,
  name,
  startDate,
  endDate,
  dayCards = [],
  dateCards = [],
  itemPerRow,
  isShowRatePeriods,
}) => {
  const { colorScheme } = useTheme();
  const formatDate = (dt: string) => dt.replace(/-/g, '/');

  const startStr = startDate
    ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
    : null;
  const endStr = endDate
    ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    : null;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        overflow: 'hidden',
        background:
          colorScheme === 'light' ? 'white' : 'var(--mantine-color-body)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: colorScheme === 'light' ? '#f9fafb' : '#1a1a1a',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: 99,
            background: ruleType === 'regular' ? '#dbeafe' : '#fef3c7',
            color: ruleType === 'regular' ? '#1d4ed8' : '#92400e',
          }}
        >
          {ruleType === 'regular' ? '一般費率' : '特殊費率'}
        </Text>
        <Text style={{ fontSize: 14, fontWeight: 600 }}>
          {name || '（未命名）'}
        </Text>
        {startStr && endStr && (
          <Text style={{ fontSize: 11, color: '#9ca3af' }}>
            {' | '}
            {formatDate(startStr)} – {formatDate(endStr)}
          </Text>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          padding: '12px 16px',
          display: 'grid',
          gridTemplateColumns: `repeat(${itemPerRow}, 1fr)`,
          gap: 10,
        }}
      >
        {ruleType === 'regular' ? (
          dayCards.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: 12,
                padding: '16px 0',
              }}
            >
              尚未設定任何費率時段
            </div>
          ) : (
            dayCards.map((card, i) => (
              <div
                key={i}
                style={{
                  border: '1px solid #f3f4f6',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                {/* DOW chips */}
                <div
                  style={{
                    padding: '6px 10px',
                    background: colorScheme === 'light' ? '#f9fafb' : '#262626',
                    display: 'flex',
                    gap: 4,
                    flexWrap: 'wrap',
                    borderBottom: '1px solid #f3f4f6',
                  }}
                >
                  {ALL_DOWS.map((dow) => {
                    const active = card.dows.includes(dow);
                    return (
                      <span
                        key={dow}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          fontSize: 11,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: active ? '#1d4ed8' : '#e5e7eb',
                          color: active ? '#fff' : '#9ca3af',
                        }}
                      >
                        {DOW_SHORT[dow]}
                      </span>
                    );
                  })}
                </div>
                {/* Slots */}
                {isShowRatePeriods && (
                  <div style={{ padding: '6px 10px' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#9ca3af',
                        marginBottom: 4,
                      }}
                    >
                      基礎費率
                    </div>
                    {card.slots.map((slot, j) => (
                      <PreviewSlotRow key={j} slot={slot} />
                    ))}
                  </div>
                )}
                {card.maxFeeSlots.length > 0 && (
                  <div
                    style={{
                      padding: '6px 10px',
                      borderTop: '1px solid #f3f4f6',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: '#9ca3af',
                        marginBottom: 4,
                      }}
                    >
                      最高收費
                    </div>
                    {card.maxFeeSlots.map((slot, j) =>
                      slot.maximumFee.map((fee, k) => {
                        const label =
                          fee.type === 'WHOLE_DAY'
                            ? '全天最高'
                            : fee.type === 'FIRST_UNIT'
                              ? `前 ${(fee as Extract<MaximumFeeDetail, { type: 'FIRST_UNIT' }>).intervalMinutes} 分鐘最高`
                              : `每 ${(fee as Extract<MaximumFeeDetail, { type: 'PER_UNIT' }>).intervalMinutes} 分鐘最高`;
                        return (
                          <div
                            key={`${j}-${k}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '2px 0',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: '#6b7280',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {k === 0
                                ? `${slot.startTime} – ${slot.endTime}`
                                : ''}
                            </span>
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                {label}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 700 }}>
                                {fee.fee}
                              </span>
                              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                元
                              </span>
                            </span>
                          </div>
                        );
                      }),
                    )}
                  </div>
                )}
              </div>
            ))
          )
        ) : dateCards.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 12,
              padding: '16px 0',
            }}
          >
            尚未設定任何費率時段
          </div>
        ) : (
          dateCards.map((card, i) => (
            <div
              key={i}
              style={{
                border: '1px solid #f3f4f6',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {/* Date chips */}
              <div
                style={{
                  padding: '6px 10px',
                  background: colorScheme === 'light' ? '#f9fafb' : '#262626',
                  display: 'flex',
                  gap: 4,
                  flexWrap: 'wrap',
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                {card.dates.map((date) => {
                  const jsDay = getDay(parseISO(date));
                  return (
                    <span
                      key={date}
                      style={{
                        padding: '2px 7px',
                        borderRadius: 99,
                        fontSize: 10,
                        fontWeight: 600,
                        background: '#fef3c7',
                        color: '#92400e',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dateToShortLabel(date)}({DOW_ZH[jsDay]})
                    </span>
                  );
                })}
              </div>
              {isShowRatePeriods && (
                <div style={{ padding: '6px 10px' }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      marginBottom: 4,
                    }}
                  >
                    基礎費率
                  </div>
                  {card.slots.map((slot, j) => (
                    <PreviewSlotRow key={j} slot={slot} />
                  ))}
                </div>
              )}
              {card.maxFeeSlots.length > 0 && (
                <div
                  style={{
                    padding: '6px 10px',
                    borderTop: '1px solid #f3f4f6',
                  }}
                >
                  <div
                    style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}
                  >
                    最高收費
                  </div>
                  {card.maxFeeSlots.map((slot, j) =>
                    slot.maximumFee.map((fee, k) => {
                      const label =
                        fee.type === 'WHOLE_DAY'
                          ? '全天最高'
                          : fee.type === 'FIRST_UNIT'
                            ? `前 ${(fee as Extract<MaximumFeeDetail, { type: 'FIRST_UNIT' }>).intervalMinutes} 分鐘最高`
                            : `每 ${(fee as Extract<MaximumFeeDetail, { type: 'PER_UNIT' }>).intervalMinutes} 分鐘最高`;
                      return (
                        <div
                          key={`${j}-${k}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '2px 0',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: '#6b7280',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {k === 0
                              ? `${slot.startTime} – ${slot.endTime}`
                              : ''}
                          </span>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>
                              {label}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700 }}>
                              {fee.fee}
                            </span>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>
                              元
                            </span>
                          </span>
                        </div>
                      );
                    }),
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ParkingRatePreviewCard;
